import {
  STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2, type PortfolioStatementImportV2,
  type StatementImportConfirmResponseBindingV2,
  type StatementImportDispositionCountsV2, type StatementImportReceiptV2,
  type StatementImportIntentResponseBindingV2, type StatementImportIntentV2,
  type StatementImportOperationStatusV2, type StatementImportStatusResponseBindingV2,
  type StatementImportUploadBindingV2,
  type StatementImportRecordV2, type StatementStagedFieldV2,
} from "./statement-import.js";

export function assertStatementImportFieldSemanticsV2(value: StatementStagedFieldV2): void {
  if (value.name === "UNCLASSIFIED" && (value.semanticRole !== "DESCRIPTIVE"
    || value.reviewState === "CONFIRMED" || value.reviewState === "CORRECTED")) invalid();
  if (value.valueState === "OBSERVED"
    && (!value.rawValue || value.normalizedValue === null || value.evidence.length === 0)) invalid();
  if (value.valueState === "OBSERVED" && value.normalizedValue !== null) assertNumericField(value);
  if (value.valueState === "UNKNOWN" && value.normalizedValue !== null) invalid();
  if (value.reviewState === "CORRECTED" && (value.extractor !== "OWNER_CORRECTION" || !value.reviewedAt)) invalid();
  if ((value.reviewState === "CONFIRMED" || value.reviewState === "REJECTED") && !value.reviewedAt) invalid();
  if (value.valueState === "OBSERVED" && value.extractor === "PDF_TEXT"
    && value.evidence.some((item) => item.page === null)) invalid();
  if (value.valueState === "OBSERVED" && value.extractor === "OCR"
    && (value.confidenceBps === null || value.evidence.some((item) => item.page === null || item.bbox === null))) invalid();
}

export function assertStatementImportRecordSemanticsV2(value: StatementImportRecordV2): void {
  if (value.fields.some((item) => item.name === "UNCLASSIFIED")
    && !value.issueCodes.includes("STATEMENT_IMPORT_FIELD_UNKNOWN")) invalid();
  if (value.disposition === "NEW" && (value.semanticRole !== "ACTIVITY_DELTA"
    || value.existingTargetIds.length > 0 || !usableEventIdentity(value))) invalid();
  if (value.disposition === "BIND_EXISTING"
    && (value.existingTargetIds.length === 0 || !usableEventIdentity(value))) invalid();
  if (value.disposition === "SNAPSHOT_ONLY" && value.semanticRole !== "ABSOLUTE_STATE") invalid();
  if ((value.kind === "POSITION_SNAPSHOT" || value.kind === "CASH_SNAPSHOT")
    && value.semanticRole !== "ABSOLUTE_STATE") invalid();
  const unresolvedInstrument = value.kind === "TRADE_ACTIVITY"
    && value.instrumentIdentity?.state !== "RESOLVED";
  if (unresolvedInstrument && (value.disposition === "NEW" || value.disposition === "BIND_EXISTING")) invalid();
  if (unresolvedInstrument && (value.disposition === "POSSIBLE_DUPLICATE" || value.disposition === "CONFLICT")
    && !value.issueCodes.includes("STATEMENT_IMPORT_INSTRUMENT_UNRESOLVED")) invalid();
  if (value.kind === "POSITION_SNAPSHOT" && value.disposition === "SNAPSHOT_ONLY"
    && value.instrumentIdentity?.state !== "RESOLVED") invalid();
  if (value.disposition === "POSSIBLE_DUPLICATE"
    && !value.issueCodes.includes("STATEMENT_IMPORT_POSSIBLE_DUPLICATE")) invalid();
  if (value.disposition === "CONFLICT" && value.issueCodes.length === 0) invalid();
}

export function assertStatementImportReadSemanticsV2(value: PortfolioStatementImportV2): void {
  if (Date.parse(value.updatedAt) < Date.parse(value.createdAt)) invalid();
  if (!allUnique(value.records.map((item) => item.recordId))) invalid();
  const fields = [...value.fields, ...value.records.flatMap((item) => item.fields)];
  if (!allUnique(fields.map((item) => item.fieldId))) invalid();
  if (fields.some((item) => item.evidence.some((locator) => locator.fileId !== value.file.fileId))) invalid();
  if (fields.some((item) => item.extractor !== "OWNER_CORRECTION" && (value.file.extractionMode === "MIXED"
    ? item.extractor !== "PDF_TEXT" && item.extractor !== "OCR" : item.extractor !== value.file.extractionMode))) invalid();
  if (value.file.extractionMode === "MIXED" && fields.length > 0
    && (!fields.some((item) => item.extractor === "PDF_TEXT") || !fields.some((item) => item.extractor === "OCR"))) invalid();
  if (!sameStatementImportDispositionCountsV2(statementImportDispositionCountsV2(value.records),
    value.reconciliation.dispositionCounts)) invalid();
  if (["READY_TO_CONFIRM", "INTENT_CREATED"].includes(value.state)) assertReady(value, fields);
}

export function assertStatementImportReceiptSemanticsV2(value: StatementImportReceiptV2): void {
  const acceptedCount = value.dispositionCounts.NEW + value.dispositionCounts.SNAPSHOT_ONLY;
  if (value.dispositionCounts.POSSIBLE_DUPLICATE > 0 || value.dispositionCounts.CONFLICT > 0) invalid();
  if (value.state === "DUPLICATE_NOOP" && (acceptedCount !== 0 || value.acceptedObservationIds.length !== 0)) invalid();
  if (value.state === "OBSERVATIONS_ACCEPTED"
    && (acceptedCount === 0 || value.acceptedObservationIds.length !== acceptedCount)) invalid();
}

export function assertStatementImportUploadBindingV2(
  actual: PortfolioStatementImportV2, expected: StatementImportUploadBindingV2,
): void {
  if (actual.bookId !== expected.bookId || actual.uploadOperation.idempotencyKey !== expected.idempotencyKey
    || actual.uploadOperation.inputFingerprint !== expected.inputFingerprint || actual.file.sha256 !== expected.fileSha256
    || actual.file.mediaType !== expected.mediaType || actual.file.bytes !== expected.bytes
    || expected.multipartFileCount !== 1 || expected.multipartTextFieldCount !== 0) invalid();
}
export function assertStatementImportIntentBindingV2(
  actual: StatementImportIntentV2, expected: StatementImportIntentResponseBindingV2,
): void {
  if (actual.importId !== expected.importId || actual.exactBookId !== expected.bookId
    || actual.inputFingerprint !== expected.inputFingerprint
    || !sameStatementImportDispositionCountsV2(actual.dispositionCounts, expected.dispositionCounts)) invalid();
}
export function assertStatementImportConfirmBindingV2(
  actual: StatementImportReceiptV2, expected: StatementImportConfirmResponseBindingV2,
): void {
  if (actual.importId !== expected.importId || actual.bookId !== expected.bookId
    || actual.inputFingerprint !== expected.inputFingerprint
    || !sameStatementImportDispositionCountsV2(actual.dispositionCounts, expected.dispositionCounts)) invalid();
}
export function assertStatementImportStatusBindingV2(
  actual: StatementImportOperationStatusV2, expected: StatementImportStatusResponseBindingV2,
): void {
  if (actual.operation !== expected.operation || (expected.importId !== null && actual.importId !== expected.importId)
    || actual.bookId !== expected.bookId || actual.idempotencyKey !== expected.idempotencyKey
    || actual.inputFingerprint !== expected.inputFingerprint) invalid();
  if (actual.receipt && (expected.dispositionCounts === null
    || !sameStatementImportDispositionCountsV2(actual.receipt.dispositionCounts, expected.dispositionCounts))) invalid();
}

export function statementImportDispositionCountsV2(records: StatementImportRecordV2[]): StatementImportDispositionCountsV2 {
  const result = { NEW: 0, BIND_EXISTING: 0, SNAPSHOT_ONLY: 0, POSSIBLE_DUPLICATE: 0, CONFLICT: 0, IGNORE: 0 };
  for (const item of records) result[item.disposition] += 1;
  return result;
}

export function sameStatementImportDispositionCountsV2(
  left: StatementImportDispositionCountsV2, right: StatementImportDispositionCountsV2,
): boolean {
  return STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2.every((key) => left[key] === right[key]);
}

export function parseCanonicalNonnegativeStatementDecimalV2(value: unknown): string {
  if (typeof value !== "string" || !/^(?:0|[1-9]\d{0,23})(?:\.\d{0,7}[1-9])?$/u.test(value)) invalid();
  return value;
}

function assertNumericField(value: StatementStagedFieldV2): void {
  const nonnegative = ["QUANTITY", "POSITION_QUANTITY", "PRICE", "COST_BASIS", "COMMISSION",
    "OTHER_FEES", "CASH_BALANCE"];
  if (nonnegative.includes(value.name)) parseCanonicalNonnegativeStatementDecimalV2(value.normalizedValue);
  if (value.name === "AMOUNT") parseCanonicalStatementDecimal(value.normalizedValue);
}
function parseCanonicalStatementDecimal(value: unknown): string {
  if (typeof value !== "string" || value === "-0"
    || !/^-?(?:0|[1-9]\d{0,23})(?:\.\d{0,7}[1-9])?$/u.test(value)) invalid();
  return value;
}

function usableEventIdentity(value: StatementImportRecordV2): boolean {
  return Boolean(value.eventIdentity?.canonicalFingerprint
    && (value.eventIdentity.strength === "STRONG" || value.eventIdentity.strength === "WEAK"));
}
function assertReady(value: PortfolioStatementImportV2, fields: StatementStagedFieldV2[]): void {
  if (value.accountIdentity.state !== "RESOLVED" || value.reconciliation.state !== "MATCHED"
    || value.reconciliation.blockingCodes.length > 0) invalid();
  if (fields.some((item) => item.valueState === "OBSERVED" && item.extractor === "OCR"
    && item.reviewState !== "CONFIRMED" && item.reviewState !== "CORRECTED")) invalid();
  if (fields.some((item) => item.name === "UNCLASSIFIED" && item.reviewState === "UNREVIEWED")) invalid();
  if (value.records.some((item) => item.disposition === "POSSIBLE_DUPLICATE"
    || item.disposition === "CONFLICT")) invalid();
}
function allUnique(values: string[]): boolean { return new Set(values).size === values.length; }
function invalid(): never { throw new Error("STATEMENT_IMPORT_RESPONSE_INVALID"); }
